import {
  Button,
  Col,
  DatePicker,
  Descriptions,
  Divider,
  Form,
  Input,
  InputNumber,
  notification,
  Row
} from 'antd';
import dayjs from 'dayjs';
import locale from 'antd/es/date-picker/locale/es_ES'
import { useContext, useEffect, useState } from 'react';
import DistributionPointSelected from '../../../context/MyDistributionPoint';
import {
  register as registerServices
} from '../../../services';
import { patternRFC } from '../../../services/utils/rfcValidator';
import request from '../../../services/request';

const DATE_FORMAT = 'DD/MM/YYYY';

const normalizePercentages = (propanePercentage = 0, butanePercentage = 0) => {
  const propane = Number(propanePercentage);
  const butane = Number(butanePercentage);
  const otherGases = 100 - (propane + butane);
  if (otherGases === 0 && propane > 0 && butane > 0) {
    return { propane, butane, otherGases };
  }
  const normalizedPropane = (propane * 100 / (propane + butane)).toFixed(3);
  const normalizedButane = 100 - normalizedPropane;
  return { propane: normalizedPropane, butane: normalizedButane, otherGases };
}

export default function DictumForm({ match, history }) {
  const [api, contextHolder] = notification.useNotification();
  const { distributionPoint } = useContext(DistributionPointSelected);
  const { id } = match.params;
  const [form] = Form.useForm();

  const [dictumIssuedDate, setDictumIssuedDate] = useState(new Date());
  const [providerRfc, setProviderRfc] = useState();

  const [normalizedValues, setNormalizedValues] = useState({ propane: 0, butane: 0, otherGases: 100 });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (id === 'new') return;
    request().get(`dictum/${id}`).then(response => {
      const { data: dictumData } = response;
      setProviderRfc(dictumData.laboratoryRfc);
      form.setFieldsValue({
        ...dictumData,
        dictumIssueDate: dayjs(dictumData.dictumIssueDate),
        testAppliedDate: dayjs(dictumData.testAppliedDate),
        sampleTakenDate: dayjs(dictumData.sampleTakenDate),
        testResultDate: dayjs(dictumData.testResultDate),
        dictumFolio: dictumData.dictumFolio.substring(dictumData.dictumFolio.length - 9, dictumData.dictumFolio.length - 4),
      });
    }).catch((error) => {
      api.error({ message: 'Error al obtener la información del dictamen', description: error })
    });
  }, [id, form, api]);

  const onValuesChange = (changedValues) => {
    if (changedValues.dictumIssueDate !== undefined) {
      setDictumIssuedDate(changedValues.dictumIssueDate.toDate());
    }
    if (changedValues.laboratoryRfc) {
      setProviderRfc(changedValues.laboratoryRfc);
    }

    setNormalizedValues(normalizePercentages(form.getFieldValue('propanePercentage'), form.getFieldValue('butanePercentage')));
  };

  const onSubmitForm = async () => {
    let dictumPayload = form.getFieldsValue();
    const computedFolio = `${providerRfc}${dictumPayload.dictumFolio.toString().padStart(5, '0')}${dictumIssuedDate.getFullYear()}`
    if (id && !(id.toLowerCase().includes("new"))) {
      dictumPayload.id = id;
    }
    dictumPayload.distributionPointId = distributionPoint.id;
    dictumPayload.propanePercentage = normalizedValues.propane;
    dictumPayload.butanePercentage = normalizedValues.butane;
    dictumPayload.dictumIssueDate = dictumPayload.dictumIssueDate.format(DATE_FORMAT);
    dictumPayload.testAppliedDate = dictumPayload.testAppliedDate.format(DATE_FORMAT);
    dictumPayload.sampleTakenDate = dictumPayload.sampleTakenDate.format(DATE_FORMAT);
    dictumPayload.testResultDate = dictumPayload.testResultDate.format(DATE_FORMAT);
    dictumPayload.dictumFolio = computedFolio;
    setLoading(true);
    registerServices.post('dictum', dictumPayload)
      .then(() => history.push('/dictums'))
      .catch((error) => api.error({ message: 'Error al guardar el dictamen', description: error }))
      .finally(() => setLoading(false));
  };

  return (
    <>
      {contextHolder}
      <Form disabled={loading} form={form} layout='vertical' onValuesChange={onValuesChange} onFinish={onSubmitForm} style={{ paddingBottom: '100px' }}>
        <Divider orientation="center" style={{ fontSize: '15px', color: '#606676' }}>Datos de identificación del laboratorio para prestar el servicio de emisión de dictámenes</Divider>
        <Row gutter={24}>
          <Col span={12}>
            <Form.Item
              label="RFC del laboratorio" className="text-wrap"
              name="laboratoryRfc"
              rules={[{ required: true, message: "Este campo es obligatorio" }, { pattern: patternRFC, message: "RFC no válido" }]}
              normalize={value => value.toUpperCase()}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Razón social laboratorio" className="text-wrap"
              name="laboratoryBusinessName"
              rules={[{ required: true, message: "Este campo es obligatorio" }]}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={24}>
          <Col span={8}>
            <Form.Item
              label="Fecha de emisión del dictamen" className="text-wrap"
              name="dictumIssueDate"
              rules={[{ required: true, message: "Este campo es obligatorio" }]}
            >
              <DatePicker locale={locale} format={DATE_FORMAT} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="Folio del dictamen" className="text-wrap"
              name="dictumFolio"
              rules={[{ required: true, message: "Este campo es obligatorio" }]}
              tooltip='Requerido para expresar el número de folio del dictamen. Deberá conformarse de los doce o trece caracteres del RFC del proveedor, seguido de cinco números únicos y consecutivos que correspondan al orden cronológico de emisión del dictamen en el año, seguidos de cuatro números que correspondan al año de emisión del mismo.'
            >
              <InputNumber min={0} max={99999} addonBefore={providerRfc} style={{ width: '100%' }} addonAfter={dictumIssuedDate.getFullYear()} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="Medio de transporte o almacenamiento del que se tomó la muestra" className="text-wrap"
              name="sampleContainerIdentification"
              rules={[{ required: true, message: "Este campo es obligatorio" }]}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>
        <Divider orientation="center" style={{ fontSize: '15px', color: '#606676' }}>Información sobre la prueba</Divider>
        <Row gutter={24}>
          <Col span={8}>
            <Form.Item
              label="Fecha de la toma de la muestra" className="text-wrap"
              name="sampleTakenDate"
              rules={[{ required: true, message: "Este campo es obligatorio" }]}
            >
              <DatePicker locale={locale} format={DATE_FORMAT} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="Fecha en que se realizaron las pruebas" className="text-wrap"
              name="testAppliedDate"
              rules={[{ required: true, message: "Este campo es obligatorio" }]}
            >
              <DatePicker locale={locale} format={DATE_FORMAT} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="Fecha en que se obtuvieron los resultados" className="text-wrap"
              name="testResultDate"
              rules={[{ required: true, message: "Este campo es obligatorio" }]}
            >
              <DatePicker locale={locale} format={DATE_FORMAT} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={24}>
          <Col span={12}>
            <Form.Item
              label="Número de lote" className="text-wrap"
              name="analizedProductBatch"
              rules={[{ required: true, message: "Este campo es obligatorio" }]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Volumen de la muestra analizada" className="text-wrap"
              name="analizedProductVolume"
              rules={[{ required: true, message: "Este campo es obligatorio" }]}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={24}>
          <Col span={12}>
            <Form.Item
              label="Método de la prueba aplicada a la muestra" className="text-wrap"
              name="testMethod"
              rules={[{ required: true, message: "Este campo es obligatorio" }]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Resultado de la prueba" className="text-wrap"
              name="testResult"
              rules={[{ required: true, message: "Este campo es obligatorio" }]}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>
        <Divider orientation="center" style={{ fontSize: '15px', color: '#606676' }}>Gas licuado de petróleo</Divider>
        <Row gutter={24}>
          <Col span={12}>
            <Form.Item
              label="Porcentaje de propano en la mezcla" className="text-wrap"
              name="propanePercentage"
              rules={[
                { required: true, message: "Este campo es obligatorio" },
                { type: 'number', min: 0, max: 100, message: 'Debe tener un valor entre 0 y 100' }]}
            >
              <InputNumber min={0} max={100} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Porcentaje de butano en la mezcla" className="text-wrap"
              name="butanePercentage"
              rules={[
                { required: true, message: "Este campo es obligatorio" },
                { type: 'number', min: 0, max: 100, message: 'Debe tener un valor entre 0 y 100' }
              ]}
            >
              <InputNumber min={0} max={100} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>
        {normalizedValues.otherGases < 100 &&
          <Row>
            <Descriptions title="Valores Normalizados">
              <Descriptions.Item label="Otros Gases">{normalizedValues.otherGases}</Descriptions.Item>
              <Descriptions.Item label="Propano">{normalizedValues.propane}</Descriptions.Item>
              <Descriptions.Item label="Butano">{normalizedValues.butane}</Descriptions.Item>
            </Descriptions>
          </Row>
        }
        <Divider orientation="center" style={{ fontSize: '15px', color: '#606676' }}>Datos del personal del proveedor que emite el dictamen</Divider>
        <Row gutter={24}>
          <Col span={12}>
            <Form.Item
              label="Apellido paterno, materno, nombre(s)" className="text-wrap"
              name="providerPersonnelFullName"
              rules={[{ required: true, message: "Este campo es obligatorio" }]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="RFC" className="text-wrap"
              name="providerPersonnelRfc"
              rules={[{ required: true, message: "Este campo es obligatorio" }, { pattern: patternRFC, message: "RFC no válido" }]}
              normalize={value => value.toUpperCase()}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>
        <Divider orientation="center" style={{ fontSize: '15px', color: '#606676' }}>Datos del representante legal del proveedor</Divider>
        <Row gutter={24}>
          <Col span={12}>
            <Form.Item
              label="Apellido paterno, materno, nombre(s)" className="text-wrap"
              name="providerLegalRepresentativeFullName"
              rules={[{ required: true, message: "Este campo es obligatorio" }]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="RFC" className="text-wrap"
              name="providerLegalRepresentativeRfc"
              rules={[{ required: true, message: "Este campo es obligatorio" }, { pattern: patternRFC, message: "RFC no válido" }]}
              normalize={value => value.toUpperCase()}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={24} style={{ textAlign: 'right' }}>
            <Button disabled={loading} type="primary" size="large" htmlType="submit">
              Guardar Dictamen
            </Button>
          </Col>
        </Row>
      </Form>
    </>
  );
}
